import { Link } from "react-router-dom";
import { ShieldCheck, ScanLine, Route, Share2, LifeBuoy, FileCheck, FileSearch, ScrollText, Siren } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead, BtnOrange } from "@/components/bits";
import { usePreRegister } from "@/components/PreRegisterModal";
import { WHATSAPP_LINK } from "@/lib/api";

const PASS = [
  { icon: ShieldCheck, t: "Identificação do motorista e veículo" },
  { icon: ScanLine, t: "Código de embarque" },
  { icon: Route, t: "Acompanhamento da corrida" },
  { icon: Share2, t: "Compartilhamento da viagem" },
  { icon: LifeBuoy, t: "Recursos de ajuda e emergência" },
];

const DRIVERS = [
  { icon: FileCheck, t: "Processo de verificação cadastral" },
  { icon: FileSearch, t: "Análise documental" },
  { icon: ScrollText, t: "Orientações de conduta" },
  { icon: LifeBuoy, t: "Recursos de suporte e emergência" },
  { icon: ShieldCheck, t: "Regras de segurança da plataforma" },
];

const ORIENTACOES = [
  "Conferir as informações da corrida;",
  "Respeitar os participantes da viagem;",
  "Utilizar os recursos de segurança quando necessário;",
  "Procurar atendimento pelos canais oficiais;",
  "Em situações emergenciais, acionar os serviços públicos competentes.",
];

function Grid({ items, testid }) {
  return (
    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid={testid}>
      {items.map((f, i) => (
        <Reveal key={f.t} delay={i * 0.06} className="h-full">
          <div className="flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/40 hover:shadow-lg">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
              <f.icon className="h-5 w-5" />
            </span>
            <p className="text-sm font-semibold leading-relaxed text-slate-800 sm:text-base">{f.t}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function Seguranca() {
  const { openPreRegister } = usePreRegister();
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Segurança"
        title="Segurança para passageiros e motoristas."
        lead="O 360Taxi busca proporcionar uma experiência de mobilidade mais organizada, com recursos tecnológicos e orientações destinadas a proteger passageiros e motoristas parceiros."
      />

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8">
        <Reveal>
          <SectionHead eyebrow="Para passageiros" title="Recursos de proteção durante a viagem." />
        </Reveal>
        <Grid items={PASS} testid="seguranca-passageiros" />

        <div className="mt-20">
          <Reveal>
            <SectionHead eyebrow="Para motoristas" title="Verificação e suporte para quem dirige." />
          </Reveal>
          <Grid items={DRIVERS} testid="seguranca-motoristas" />
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10" data-testid="orientacoes-gerais">
            <div className="flex items-center gap-3">
              <Siren className="h-6 w-6 text-brand-orange" />
              <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">Orientações gerais</h3>
            </div>
            <ul className="mt-6 space-y-2.5">
              {ORIENTACOES.map((o) => (
                <li key={o} className="flex items-start gap-3 text-base text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                  {o}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-slate-500">
              Recursos em desenvolvimento não são divulgados como funcionalidades ativas, e promessas de
              monitoramento humano permanente só serão publicadas após confirmação da operação.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-center gap-6 rounded-3xl bg-brand-obsidian px-6 py-14 text-center sm:px-12">
            <h2 className="max-w-xl font-display text-2xl font-extrabold text-white sm:text-3xl">
              Precisa de ajuda ou quer falar sobre segurança?
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row">
              <BtnOrange as={Link} to="/suporte" data-testid="seguranca-cta-suporte">
                Central de suporte
              </BtnOrange>
              <BtnOrange as="a" href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="seguranca-cta-whatsapp" className="!bg-emerald-500 hover:!bg-emerald-600">
                WhatsApp oficial
              </BtnOrange>
            </div>
            <p className="max-w-lg text-xs leading-relaxed text-slate-400">
              Em emergências, acione imediatamente os serviços públicos competentes.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
